import { UpperCasePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  HostListener,
  inject,
  Signal,
  signal,
  WritableSignal
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import {
  AppLanguage,
  AppRoutes,
  AUTH_CLIENT_TOKEN,
  AuthClientProvider,
  AuthRoutes,
  ENV_CONFIG,
  EnvConfig,
  ProfileRoutes,
  TranslationService,
  UserStateService
} from '@core';
import { getNewVersionNative } from '@update/application';
import { UpdateService } from '@update/infra';
import { SzIconComponent } from '../../display/sz-icon';

@Component({
  selector: 'sz-profile-menu',
  templateUrl: './sz-profile-menu.component.html',
  styleUrls: ['./sz-profile-menu.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [TranslateModule, UpperCasePipe, SzIconComponent]
})
export class SzProfileMenuComponent {
  private readonly authClient: AuthClientProvider = inject(AUTH_CLIENT_TOKEN);
  private readonly userState: UserStateService = inject(UserStateService);
  private readonly router: Router = inject(Router);
  private readonly translation: TranslationService = inject(TranslationService);
  private readonly updateService: UpdateService = inject(UpdateService);
  private readonly env: EnvConfig = inject(ENV_CONFIG);

  readonly open: WritableSignal<boolean> = signal<boolean>(false);
  readonly currentLang: Signal<AppLanguage> = this.translation.currentLanguage;
  readonly hasOptionalUpdate: Signal<boolean> = toSignal(this.updateService.hasOptionalUpdate(), {
    initialValue: false
  });

  // TODO: tymczasowo wymuszony fallback (inicjały) - avatar z photoUrl wyłączony do naprawy.
  // Przywrócić: return url && url.trim().length > 0 ? url : null;
  readonly avatarUrl: Signal<string | null> = computed((): string | null => null);

  readonly initial: Signal<string> = computed((): string => {
    const name: string = (this.userState.user()?.displayName ?? '').trim();
    if (name.length === 0) {
      return 'SZ';
    }
    const parts: ReadonlyArray<string> = name.split(/\s+/).filter((p: string): boolean => p.length > 0);
    if (parts.length >= 2) {
      return (parts[0].charAt(0) + parts[1].charAt(0)).toUpperCase();
    }
    return parts[0].charAt(0).toUpperCase();
  });

  readonly displayName: Signal<string | null> = computed((): string | null => {
    const name: string = (this.userState.user()?.displayName ?? '').trim();
    return name.length > 0 ? name : null;
  });

  readonly email: Signal<string | null> = computed((): string | null => {
    const e: string = (this.userState.user()?.email ?? '').trim();
    return e.length > 0 ? e : null;
  });

  toggle(): void {
    this.open.update((value: boolean): boolean => !value);
  }

  close(): void {
    this.open.set(false);
  }

  selectMyProfile(): void {
    this.close();
    void this.router.navigateByUrl(`/${AppRoutes.PROFILE}`);
  }

  selectSignOut(): void {
    this.close();
    void this.signOutAndRedirect();
  }

  selectChangeLanguage(): void {
    this.close();
    void this.router.navigateByUrl(`/${AppRoutes.LANG}`);
  }

  selectPartners(): void {
    this.close();
    void this.router.navigateByUrl(`/${AppRoutes.PARTNERS}`);
  }

  selectContact(): void {
    this.close();
    void this.router.navigate([AppRoutes.PROFILE, ProfileRoutes.CONTACT]);
  }

  selectWhatsNew(): void {
    this.close();
    void this.router.navigate([AppRoutes.PROFILE, ProfileRoutes.WHATS_NEW]);
  }

  selectDownloadUpdate(): void {
    this.close();
    getNewVersionNative(this.env);
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.open()) {
      this.close();
    }
  }

  private async signOutAndRedirect(): Promise<void> {
    await this.authClient.signOut();
    await this.router.navigateByUrl(`/${AppRoutes.AUTH}/${AuthRoutes.WELCOME}`);
  }
}
